
import { defineComponent, computed, ref, onMounted,  type PropType} from 'vue';
import useEsignCommon, { stringToLanguage } from '@/eSign-i18n';
import axios from 'axios';
import PdfViewer from './pdf.vue';
import { SignerSignFlowStatus, UserIdentifierType, ScsFlowStatus, Procuration, 
  UploadSignatureModel, SigningInfoDetails, ESignCommonClient, SigningRole, 
  SignatureRequestType } from './esignclient';
import { ESignClient2 } from './esignclient2';
import TopMenu from './topmenu.vue';
import Layout from './layout.vue';
import SCS, { type ScsResponse } from '../scs';

import { useRouter} from 'vue-router';
import { useI18n } from '@/i18n-shim';
import { userStore, ensureUser, clearUser } from '@/userStore';

import DataList from '@/components/DataList.vue';


interface AttachmentView {
  id: string;
  fileName: string;
  loadedUrl: string | null;
  contentType: string | undefined;
}

function isIE11() { return !window.fetch; }
export default defineComponent({
  name: 'SignView',

  components: {
    pdfviewer: PdfViewer,
    topmenu: TopMenu,
    layout: Layout,
    DataList: DataList,
  },
  props: {
    tenantId: {
      type: String,
      required: true,
    },
    signFlowId: {
      type: String,
      required: true,
    },
    signatureRequestType: {
      type: String as PropType<SignatureRequestType>,
      required: true,
    },
    signatureId: {
      type: String,
      required: true,
    },
    loggedOutReason: {
      type: String,
      required: false,
    },
    isTimeoutTest: {
      type: Boolean,
      required: false,
    }
  },
  setup(props) {
    const rootUri = computed(() => {
      return `/api/${props.tenantId}/signflows/v2/${props.signFlowId}/${props.signatureRequestType}/${props.signatureId}`;
    });
    const isTest = computed(() => props.signFlowId === 'test');

    const isViewer = computed(() => {
      //the prop comes from route and can be in any case
      return props.signatureRequestType.toLowerCase() == SignatureRequestType.View.toLowerCase();
    });

    // How many seconds before the access token expires should a new one be retrieved with the refresh token?
    //const accessTokenExpiringRefreshTime = () => props.isTimeoutTest ? 20 : 60;

    // Feature switch for signfile polling, remove and default to true once migrated
    const isSignFilePollingEnabled = ref(false);
    const signerSignFlowStatus = ref<SignerSignFlowStatus | null>(null);
    const isDocumentViewerLoaded = ref(false);
    const isAttachmentViewerLoaded = ref<boolean[]>([]);
    const page = ref<'signing-info' | 'document' | 'initialLoading' | 'loading' | 'unauthorizedError' | 'signFlowCanceledError' | 'signFlowRejectedBySignerError' | 'identityServerError' | 'empty' | ''>('');
    const loadedSignFileUrl = ref<string | null>(null);

    const isScsStarted = ref(false);
    const loginInfo = computed(() => userStore.loginInfo);
    const dialog = ref<null | 
      'signed' | 
      'signingInProgress' | 
      'signingFailed' | 
      'rejectionInProgress' | 
      'rejected' | 
      'rejectionFailed' | 
      'sign' | 
      'reject' | 
      'zippingInProgress'
      >(null);
    const bytesToSignB64 = ref<string>('');
    const signFileId = ref('');
    const signFlowCancellationComment = ref<string | undefined>(undefined);
    const useScsForSigning = ref(false);
    const signingRole = ref<SigningRole |null>(null);
    const authenticationUri = ref('');
    const userIdentifierType = ref<UserIdentifierType>(UserIdentifierType.Unknown);
    const isAlreadySigned = ref(false);
    const scsFlowId = ref('');
    const attachments = ref<AttachmentView[] | null>(null);
    const signingInfo = ref<SigningInfoDetails | null>(null);
    const rejectionComment = ref('');
    const selectedAttachmentId = ref<string | null>(null);
    const uiVersion = ref<number | null>(null);
    const isSigningTimedOut = ref(false);
    const procurationEnabled = ref(false);
    const showSigningInfoTab = ref(false);
    const zipFilesAfterFinalization = ref(false);
    const selectedProcuration = ref<Procuration |null>(null);
    const procs = Procuration;
    const signingRoles = SigningRole;
    const unauthorizedErrorMessageIdentifier = ref('');

    // A locale needs to be set or i18n defaults to en-US during the time the tenant-specific 
    // language list is loaded(no text should be visible during the loading period, 
    // but things such as header logo alt will be affected - alternatively could wait for the 
    // language list to load before displaying anything(but that's worse UX) or use a blank alt)
    //const tenantAvailableLanguages = ref<string[]>(LanguageConfiguration.allLanguages);

    const selectedAttachment = computed(() => {
      const attachmentId = selectedAttachmentId.value;
      if (attachments.value && attachmentId) {
        return attachments.value.find(x => x.id === attachmentId)?.loadedUrl;
      } else {
        return null;
      }
    });

    const signFileVersion = computed(() => isSignFilePollingEnabled.value && signerSignFlowStatus.value === 'Finalized' ? 2 : 0);

    const isSignFlowAlreadySignedAndWaitingForFinalization = computed(() =>
      signerSignFlowStatus.value === 'AlreadySignedAndWaitingForFinalization');
    const isSignFlowAlreadySignedAndWaitingForOtherSigners = computed(() =>
      signerSignFlowStatus.value === 'AlreadySignedAndWaitingForOtherSigners');
    const isSignFlowValidating = computed(() =>
      signerSignFlowStatus.value === 'Validating');
    const isSignFlowFinalized = computed(() =>
      signerSignFlowStatus.value === 'Finalized');
    const isSignFlowCancelled = computed(() =>
      signerSignFlowStatus.value === 'Cancelled');
    const isSignFlowNotFound = computed(() =>
      signerSignFlowStatus.value === 'NotFound');
    const isSignFlowRejectedBySigner = computed(() =>
      signerSignFlowStatus.value === 'RejectedBySigner' || signerSignFlowStatus.value === 'Failed');

    const i18n = useI18n();

    const esigncommon = useEsignCommon();


    const availableLanguages = computed({
      get: () => esigncommon.availableLanguages.value,
      set: (value) => {
        esigncommon.availableLanguages.value = value;
        // If the tenant doesn't allow the default language, switch to another
        // Note: this logic should probably be in esign-i18n but since this is currently the only place where languages are set, let's keep it here
        const typedLang = stringToLanguage(i18n.locale.value);
        if (!typedLang)
          return;
        if(esigncommon.availableLanguages.value.indexOf(typedLang) < 0) {
          setLanguage(esigncommon.availableLanguages.value[0]);
        }
      }
    });


    function setLanguage(lang: string) {
      const typedLang = stringToLanguage(lang);
      if (!typedLang)
        return
      esigncommon.setLanguage(typedLang);
    }

    function attachmentOnClick(attachment: AttachmentView) {
      if (attachmentIsPdf(attachment.contentType)) {
        selectedAttachmentId.value = attachment.id;
      } else {
        downloadAttachment(attachment.id);
      }
    }

    function attachmentIsPdf(contentType: string | undefined) {
      if (contentType == 'application/pdf')
        return true;
    }
    const router = useRouter();
    function openTab(tab: 'signing-info' | 'document' | 'attachments') {
      currentTab.value = tab;
    }

    function showInitialLoading() { page.value = 'initialLoading'; }
    function showLoading() { page.value = 'loading'; }

    function showLogin(timedOutError: boolean) {
      //todo: check if there is a need to pass timeouterror or "props.loggedOutReason" to login page
      /*
      const args: any = { extraQueryParams: {} };
      // Supply IdentityServer with a logout reason (if this was a regular logout or one triggered because the user was in the middle of a signing flow and was interrupted by a timeout)
      if (props.loggedOutReason) { args.extraQueryParams.reason = props.loggedOutReason; }
      // When the access token has expired, redirect to login page, with a parameter designating the login page is displayed because the token had expired (so it can be shown in UI)
      if (timedOutError) { args.extraQueryParams.reason = 'expired'; }
      // Redirect to IdentityServer login
      //userManager!.signinRedirect(args);
      */

      router.replace({ name: 'login',
        params:  {
          tenantId:props.tenantId,
          signFlowId: props.signFlowId,
          signatureRequestType: props.signatureRequestType,
          signatureId: props.signatureId,
        }
    });
    }

    // Show the signfile viewer page, and *then* start loading the signfile uri (which takes a moment as it requires fetching a file access token) - in this order so the user sees the page immediately
    function showDocument(signFileUrlLoader?: () => Promise<string>) {
      if (signFileUrlLoader) {
        loadedSignFileUrl.value = null;

        //intentionally not awaiting here
        signFileUrlLoader().then((url) => loadedSignFileUrl.value = url);
        
      }
      page.value = 'document';

      
    }

    function showSignedPage() {
      if (isSignFilePollingEnabled.value) {
        // Poll for the post-signing signflow status and close the signing in progress dialog when first receiving a new status
        showFinalizedDocumentWhenReady(() => {
          dialog.value = null;
        });
      } else {
        // Legacy signed dialog
        dialog.value = 'signed';
      }
    }

    function showSigningInProgressDialog() { dialog.value = 'signingInProgress'; }
    function showSigningFailedDialog() { dialog.value = 'signingFailed'; }
    function showRejectionInProgressDialog() { dialog.value = 'rejectionInProgress'; }
    function showRejectedDialog() { dialog.value = 'rejected'; }
    function showRejectionFailedDialog() { dialog.value = 'rejectionFailed'; }
    function showZippingInProgressDialog() { dialog.value = 'zippingInProgress'; }
    function showSignDialog() { dialog.value = 'sign'; }
    function showRejectDialog() { dialog.value = 'reject'; }
    function closeDialog() { dialog.value = null; }

    function sleep(ms: number) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    }

    async function showFinalizedDocumentWhenReady(firstLoadCallback?: () => void) {
      let firstLoad = true;
      let pollingRate = 2000;
      const pollingRateIncrementFactor = 1.3; // add 30% to the polling rate every retry
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const prevSignFileVersion = signFileVersion.value;
        await loadSignature();
        if (firstLoad && firstLoadCallback) {
          firstLoadCallback();
          firstLoad = false;
        }
        pollingRate *= pollingRateIncrementFactor;
        switch (signerSignFlowStatus.value) {
        case 'AlreadySignedAndWaitingForOtherSigners':
          // Another signer hasn't completed their signature, continue polling
          pollingRate = Math.min(pollingRate, 60000); // max out at 60s polling rate
          break;
        case 'AlreadySignedAndWaitingForFinalization':
          // The document is being finalized, continue polling
          pollingRate = Math.min(pollingRate, 10000); // max out at 10s polling rate
          break;
        case 'Validating':
          // The document is being validated, continue polling
          pollingRate = Math.min(pollingRate, 10000); // max out at 10s polling rate
          break;
        case 'Finalized':
        case 'Cancelled':
        case 'RejectedBySigner':
        case 'Failed':
          // If the signfile version has changed, show the new one
          if (signFileVersion.value !== prevSignFileVersion) {
            showDocument(async () => {
              
              return signFileUri(signFileVersion.value);
            });
          }
          // The signflow has completed in some way, stop polling
          return;
        }
        await sleep(pollingRate);
      }
    }

    async function showDocumentWhenValidationComplete() {
      let pollingRate = 2000;
      const pollingRateIncrementFactor = 1.3; // add 30% to the polling rate every retry
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await loadSignature();
        pollingRate *= pollingRateIncrementFactor;
        switch (signerSignFlowStatus.value) {
        case 'Validating':
          // The document is being validated, continue polling
          pollingRate = Math.min(pollingRate, 10000); // max out at 10s polling rate
          break;
        default:
          // The validation has completed in some way, stop polling and show signing page
          showSigningPage();
          return;
        }
        await sleep(pollingRate);
      }
    }
    
    //axios lifetime seems to be different (most likely longer) than this component's lifetime. Clear interceptors to avoid memory leaks, but also to prevent double handling of errors.
    //since we are saying Promise.reject(<string>) for some reason, a double handler would get the string as the error instead of the error object.
    //this can be triggered for example by first going to a forbidden signflow, then to info page, and then back with the back button.
    axios.interceptors.response.clear();
    axios.interceptors.response.use((response) => response, (error) => {
      
      switch (error?.response?.status) {
        case 419:
          // Timeout - show login and throw an exception to abort any ongoing method
          showLogin(true);
          return Promise.reject();
        case 401:
          //Unauthorized - show login                   
          page.value = 'unauthorizedError';
          clearUser();
          showLogin(false);
          return Promise.reject('unauthorized');
        case 403:
          // Forbidden - show error page and throw an exception to abort any ongoing method
          if (error?.response?.data?.reason)
          {
            page.value = 'unauthorizedError';
            unauthorizedErrorMessageIdentifier.value = 'Unauthorized.' + error.response.data.reason;
            return Promise.reject('forbidden');
          }
          page.value = 'unauthorizedError';
          showLogin(false);
          return Promise.reject('unauthorized');
        case 404:
          // Not found
          signerSignFlowStatus.value = SignerSignFlowStatus.NotFound;
          return Promise.reject('not found');
        default:
          // Resolve to pass the error handling to ESignClient
          return Promise.resolve(error);
      }
    });
    
    const signClient = new ESignClient2('', axios);

    onMounted(() => {
      if (isIE11()) {
        page.value = 'empty';
        return;
      }

      //if (this.$route.query.uiVersion) { this.uiVersion = parseInt(this.$route.query.uiVersion[0]!, 10) || this.uiVersion; }

      showInitialLoading();
      
      getInitParams().then((params) => {
        if (!params) {
          signerSignFlowStatus.value = SignerSignFlowStatus.NotFound;
          return;
        }

        //availablelanguages in the api cannot be typed as strictly as in typescript, so let's use map to fix typings
        availableLanguages.value = params.availableLanguages.map(lang => lang as "fi-FI" | "sv-FI" | "en-US");
        authenticationUri.value = params.authenticationUri;
        uiVersion.value = params.uiVersion;
        isSignFilePollingEnabled.value = params.signFilePolling;
        procurationEnabled.value = params.procurationEnabled;
        showSigningInfoTab.value = params.showSigningInfoTab;
        currentTab.value = showSigningInfoTab.value ? 'signing-info' : 'document';
        zipFilesAfterFinalization.value = params.zipFilesAfterFinalization;
        if (!params.signatureRequestExists) {
          signerSignFlowStatus.value = SignerSignFlowStatus.NotFound;
        }
        else if (!isTest.value) {
          loginAndThenLoadSignatureAndShowSigningPage().catch(err => {
            console.error("Error in loginAndThenLoadSignatureAndShowSigningPage", err);
          });
        } else {
          setLanguage(availableLanguages.value[0]);
          loadSignatureAndThenShowSigningPage();
        }

        //axios.interceptors.request.use(async (request) => {
        //  request.headers.Authorization = 'Bearer ' + (await accessToken());
        //  return request;
        //});

      }).catch(err => {
        console.error("Error getting or handling init params", err);
      });
    });

    async function logout() {
      if (!isTest.value) {
        router.push({name: 'logout', params: { tenantId: props.tenantId }, query: { loginUrl: loginUrl.value }});
      }
    }

    async function logoutTimeout() {
      if (!isTest.value) {
        //const langQueryPart = `?lang=${i18n.locale.value}`;
        //userManager!.signoutRedirect({
        //  post_logout_redirect_uri: `${hostUri()}/sign/${props.tenantId}/${props.signFlowId}/${props.signatureId}/logout/timeout${langQueryPart}`
        //});
      }
    }

    async function waitForCoverPage() {
      await sleep(10000);
      for (let i = 0; i < 150; i++) {
        const scsStatus = await signClient.scsStatus(props.tenantId, props.signFlowId, props.signatureId, props.signatureRequestType, scsFlowId.value);
        if (scsStatus === ScsFlowStatus.CoverPageGenerated) {
          showDocument(async () => {
            return scsFlowCoverPageUri();
          });
          return true;
        }
        if (scsStatus === ScsFlowStatus.Failed) {
          return false;
        }
        await sleep(2000);
      }
      return false;
    }

    async function loginAndThenLoadSignatureAndShowSigningPage() {
      await loadUser();
      if(loginInfo.value.userIdentifierType === UserIdentifierType.Unknown) {
        //early return to avoid generating 401's in monitoring
        showLogin(false);
        return;
      }
      
      if (showSigningInfoTab.value) {
        await loadSigningInfoDetails();
      }
      await loadSignature();
      await showSigningPage();
    }

    async function loadUser() {
      await ensureUser(props.tenantId);
    }

    function completionTimeFormat(date: Date) {
      if(date == null) return null;

      const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds()));

      const formattedDate = utcDate.toLocaleString(i18n.locale.value, { 
        timeZone: 'Europe/Helsinki',
        year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hour12: false
      });

      return formattedDate
    }

    async function loadSignatureAndThenShowSigningPage() {
      await loadSignature();
      await showSigningPage();
    }

    async function loadSigningInfoDetails() {
      
      signingInfo.value = await signClient.getSigningInfoDetails(props.tenantId, props.signFlowId, props.signatureId, props.signatureRequestType)
    }

    async function loadSignature() {
      if(isViewer.value) {
        const signature = await signClient.getSignature(props.tenantId, props.signFlowId, props.signatureId, props.signatureRequestType);
        if (signature.currentUserIdentifierType) { userIdentifierType.value = signature.currentUserIdentifierType; }
        if (signature.signerSignFlowStatus) { signerSignFlowStatus.value = signature.signerSignFlowStatus; }
        if (signature.cancellationComment) { signFlowCancellationComment.value = signature.cancellationComment; }
      } else {
        const signature = await signClient.getSignature(props.tenantId, props.signFlowId, props.signatureId, props.signatureRequestType);
        if (signature.currentUserIdentifierType) { userIdentifierType.value = signature.currentUserIdentifierType; }
        if (signature.signerSignFlowStatus) { signerSignFlowStatus.value = signature.signerSignFlowStatus; }
        if (signature.cancellationComment) { signFlowCancellationComment.value = signature.cancellationComment; }
        if (signature.useScsForSigning) { useScsForSigning.value = userIdentifierType.value === UserIdentifierType.Scs; }
        if (signature.signingRole) { signingRole.value = signature.signingRole; }
  
        // Legacy signed page, remove when migrated to new signed page
        if (!isSignFilePollingEnabled.value) {
          switch (signature.signerSignFlowStatus) {
          case SignerSignFlowStatus.Finalized:
          case SignerSignFlowStatus.AlreadySignedAndWaitingForFinalization:
          case SignerSignFlowStatus.AlreadySignedAndWaitingForOtherSigners:
            isAlreadySigned.value = true;
            break;
          }
        }
      }
    }

    async function showSigningPage() {
      // If an error page is being displayed, terminate as there is no need to list documents and attachments
      if (isSignFlowNotFound.value || isSignFlowCancelled.value || isSignFlowRejectedBySigner.value) {
        return;
      }

      if (isSignFlowValidating.value) {
        showDocumentWhenValidationComplete();
        return;
      }

      // If the user has already signed but the document is still collecting signatures, poll to show the finalized document when ready
      if (isSignFlowAlreadySignedAndWaitingForFinalization.value || isSignFlowAlreadySignedAndWaitingForOtherSigners.value) {
        showFinalizedDocumentWhenReady();
      }

      signClient.listSignFiles(props.tenantId, props.signFlowId, props.signatureId, props.signatureRequestType).then(async (signFileIds) => {
        for (const id of signFileIds) {
          signFileId.value = id;
          showDocument(async () => {
            return signFileUri(signFileVersion.value);
          });
          // TODO: handle multiple signfiles
          break;
        }
      });

      signClient.listAttachments(props.tenantId, props.signFlowId, props.signatureId, props.signatureRequestType).then((attachmentList) => {
        const a = new Array<Promise<AttachmentView>>();
        for (const attachmentListItem of attachmentList) {
          a.push((async () => {
            const attachment: AttachmentView = {
              id: attachmentListItem.id,
              fileName: attachmentListItem.fileName,
              loadedUrl: attachmentUri(attachmentListItem.id),
              contentType: attachmentListItem.contentType
            };
            return attachment;
          })());
        }
        Promise.all(a).then((attachmentsDto: AttachmentView[]) => {
          attachments.value = attachmentsDto;
        });
      });
    }

    async function onReject() {
      showRejectionInProgressDialog();
      let isSuccess = false;
      try {
        await signClient.rejectSignature(props.tenantId, props.signFlowId, props.signatureId, props.signatureRequestType, rejectionComment.value);
        isSuccess = true;
      } catch { 
        isSuccess = false;
      }
      if (isSuccess) {
        showRejectedDialog();
      } else {
        showRejectionFailedDialog();
      }
    }

    async function onSign() {
      showSigningInProgressDialog();
      let isSuccess = false;
      try {
        const signatureModel = new UploadSignatureModel({
          proc: selectedProcuration.value || Procuration.None
        })
        await signClient.uploadSignature(props.tenantId, props.signFlowId, props.signatureId, props.signatureRequestType, signatureModel);
        isSuccess = true;
      } catch { 
        // intentionally empty
      }
      if (isSuccess) {
        showSignedPage();
      } else {
        showSigningFailedDialog();
      }
    }

    async function onBeginScs() {
      showLoading();
      scsFlowId.value = await signClient.startScsSignature(signFileId.value, props.tenantId, props.signFlowId, props.signatureId, props.signatureRequestType);
      let isSuccess = await waitForCoverPage();
      if (isSuccess) {
        try {
          await initFinalizeScs();
        } catch { isSuccess = false; }
      }
      if (!isSuccess) {
        showSigningFailedDialog();
      }
    }

    async function initFinalizeScs() {
      isScsStarted.value = true;

      const scsFlowBytesResponse = await signClient.downloadScsFlowBytesToSignFile(props.tenantId, props.signFlowId, props.signatureId, props.signatureRequestType, scsFlowId.value);
      const bytesToSign = await scsFlowBytesResponse.data;

      const convertBlobToBase64 = (blob: Blob) => new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onerror = reject;
        reader.onload = () => {
          // disregard mimetype and encoding
          if (!reader.result) {
            return;
          }
          const base64 = (reader.result as string).split(',')[1];
          resolve(base64);
        };
        reader.readAsDataURL(blob);
      });

      bytesToSignB64.value = await convertBlobToBase64(bytesToSign);
      showDocument();
    }

    async function onFinishScs() {
      showLoading();

      const handleScsResponse = async (response: ScsResponse) => {
        let isSuccess = false;
        if (response.status === 'ok' && response.signature) {
          try {
            // TODO: test to see if 'Content-Type': 'application/json' is needed
            await signClient.finishScsSignature(props.tenantId, props.signFlowId, props.signatureId, props.signatureRequestType, scsFlowId.value, response.signature);
            isSuccess = true;
          } catch {
            // intentionally empty
           }
        }
        showDocument();
        if (isSuccess) {
          showSignedPage();
        } else {
          showSigningFailedDialog();
        }
      };

      const versionInfo = SCS.getVersionInfo();
      if (!versionInfo || !('version' in versionInfo))
        throw new Error('SCS version info does not contain version property');
      
      const reportedScsVersion = String(versionInfo.version);
      if (!reportedScsVersion)
        throw new Error('SCS version missing');

      const request = {
        selector: { keyusages: ['nonrepudiation'] },
        content: bytesToSignB64.value,
        contentType: 'digest',
        //20.1.2025: Changed SHA-256 to SHA256 to match the spec (and Atostek's implementation)
        hashAlgorithm: 'SHA256',
        //20.1.2025: Changed pkcs7 to cms to match the spec (and Atostek's implementation)
        signatureType: 'cms',
        version: reportedScsVersion,
      };
      SCS.sign(handleScsResponse, request);
    }

    async function downloadFinalizedDocument() {
      const uri = signFileDownloadUri();
      const link = document.createElement('a');
      link.href = uri;
      link.click();
    }

    // First sends request to check status of zipfile generation
    // if completed only then provide download uri to the user
    async function downloadFinalizedZip() {      
      const isReady = await signClient.getZipStatus(props.tenantId, props.signFlowId, props.signatureId, props.signatureRequestType);

      if(isReady) {
        const uri = zipPackageDownloadUri();
        const link = document.createElement('a');
        link.href = uri;
        link.click();
      } else {
        showZippingInProgressDialog();
      }
    }

    function downloadAttachment(attachmentId: string) {      
      const link = document.createElement('a');
      link.href = attachmentUri(attachmentId);;
      link.click();
    }
   
    const scsFlowCoverPageUri = () =>
      `${rootUri.value}/scsflowfiles/${scsFlowId.value}/coverpage`;
    const signFileUri = (version: number) =>
      `${rootUri.value}/signfiles/${signFileId.value}/${version}`;

    const signFileDownloadUri = () =>
      `${rootUri.value}/signfiles/${signFileId.value}/download`;

    const zipPackageDownloadUri = () =>
      `${rootUri.value}/zip/download`;

    const attachmentUri = (attachmentId: string) =>
      `${rootUri.value}/attachments/${attachmentId}`;

    //async function accessToken() {
    //  const user = await userManager!.getUser();
    //  return user ? user.access_token : null;
    //}

    async function getInitParams() {      
      const commonClient = new ESignCommonClient();
      return await commonClient.tenantInfoForSignature(props.tenantId, props.signFlowId, props.signatureId, props.signatureRequestType);
    }

    interface Participant {
      name?: string;
      signingRole?: string;
      completionTime?: Date | null;
    }

    interface DataListEntry {
      label: string;
      value: string;
    }

    const loginUrl = computed(() => userStore.loginInfo.loginUrl);

    const invitationDetails = computed<DataListEntry[]>(() => {
      const output: DataListEntry[] = [];
      if (signingInfo.value?.signflowName) {
        output.push({ label: i18n.t('SigningInfoSignflowName'), value: signingInfo.value.signflowName });
      }
      if (signingInfo.value?.inviterName) {
        output.push({ label: i18n.t('SigningInfoInviterName'), value: signingInfo.value.inviterName });
      }
      if (signingInfo.value?.inviterEmail) {
        output.push({ label: i18n.t('SigningInfoInviterEmail'), value: signingInfo.value.inviterEmail });
      }
      if (signingInfo.value?.invitationMessage) {
        const msgLines = signingInfo.value.invitationMessage.trim().split('\n');
        for (let i = 0; i < msgLines.length; i++) {
          // Label the first line
          output.push({ label: i === 0 ? i18n.t('SigningInfoInvitationMessage') : '', value: msgLines[i] });
        }
      }
      return output;
    });

    const signerDetailsCollected = computed<DataListEntry[]>(() => {
      const output: DataListEntry[] = [
        { label: i18n.t('SigningInfoName'), value: loginInfo.value?.displayName ?? '' },
        { label: i18n.t('UserIdentifierType_' + (loginInfo.value?.userIdentifierType ?? '')), value: loginInfo.value?.userIdentifier ?? '' },
      ];
      if (signingInfo.value?.organisation) {
        output.push({ label: i18n.t('SigningInfoOrganisation'), value: signingInfo.value.organisation });
      }
      if (signingInfo.value?.role) {
        output.push({ label: i18n.t('SigningInfoRole'), value: signingInfo.value.role });
      }
      return output;
    });

    const approvers = computed<DataListEntry[]>(() => {
      const input = signingInfo.value?.signers?.filter(x => x.signingRole == signingRoles.Approver);
      return participantDataList(input);
    });
    
    const signers = computed<DataListEntry[]>(() => {
      const input = signingInfo.value?.signers?.filter(x => x.signingRole == signingRoles.Signer);
      return participantDataList(input);
    });
    
    const viewers = computed<DataListEntry[]>(() => {
      const input = signingInfo.value?.viewers;
      return participantDataList(input);
    });

    function participantDataList(input: Participant[] | undefined | null): DataListEntry[] {
      const output: DataListEntry[] = [];
      for (const participant of input ?? []) {
        const name = participant.name || i18n.t('SigningInfoNoName');
        let status = '';
        if (participant.signingRole) {  // N/A for viewers
          status = participant.completionTime
          ? (i18n.t('SigningInfoStatusDone_' + participant.signingRole) + ' ' + completionTimeFormat(participant.completionTime))
          : i18n.t('SigningInfoStatusPending_' + participant.signingRole);
        }
        output.push({ label: name, value: status });
      }
      return output;
    }

    const currentTab = ref<'signing-info' | 'document' | 'attachments'>(showSigningInfoTab.value ? 'signing-info' : 'document');
    
    return {
      page,
      loginInfo,
      logout,
      isSignFlowNotFound,
      isSignFlowCancelled,
      isSignFlowValidating,
      isSignFlowRejectedBySigner,
      dialog,
      uiVersion,
      isAlreadySigned,
      isSignFlowFinalized,
      isSignFlowAlreadySignedAndWaitingForFinalization,
      isSignFlowAlreadySignedAndWaitingForOtherSigners,
      showRejectDialog,
      useScsForSigning,
      onBeginScs,
      onFinishScs,
      isScsStarted,
      showSignDialog,
      attachments,
      attachmentOnClick,
      attachmentIsPdf,
      attachmentUri,
      showSigningInfoTab,
      zipFilesAfterFinalization,
      signingInfo,
      loadedSignFileUrl,
      selectedAttachmentId,
      selectedAttachment,
      isAttachmentViewerLoaded,
      isDocumentViewerLoaded,
      openTab,
      currentTab,
      rejectionComment,
      closeDialog,
      onReject,
      isSigningTimedOut,
      procurationEnabled,
      selectedProcuration,
      onSign,
      downloadFinalizedDocument,
      downloadFinalizedZip,
      logoutTimeout,
      procs,
      signingRole,
      signingRoles,
      isViewer,
      ...useI18n(),
      completionTimeFormat,
      invitationDetails,
      signerDetailsCollected,
      approvers,
      signers,
      viewers,
      unauthorizedErrorMessageIdentifier
    }
  }
});
